import type { FunctionComponent, ReactNode } from 'react';
import {
  defineMessages,
  FormattedMessage,
  MessageDescriptor,
} from 'react-intl';
import { Stack, Text } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';

import ErrorImage from '#assets/error.svg';
import { imageContainerStyling, RAISE_SUPPORT_TICKET_LINK } from './ErrorPage';

const errorMessages = defineMessages({
  genericHeading: {
    id: 'error.generic.heading',
    defaultMessage: 'Something went wrong',
    description: 'Vague error message that an issue occurred',
  },
  genericFooter: {
    id: 'error.generic.footer',
    defaultMessage: 'Raise a support request',
    description: 'User should raise a support request',
  },
  genericErrorInformation: {
    id: 'error.generic.information',
    defaultMessage: 'INFORMATION FOR THE OWNER OF {client}',
  },
});

type DescriptionType = string | MessageDescriptor | ReactNode | ReactNode[];

const GenericErrorPage: FunctionComponent<{
  clientName?: string;
  description: DescriptionType;
}> = ({ clientName, description }) => {
  const descriptionElement = () => {
    if (typeof description === 'string') {
      return description;
    }
    if (description == null) {
      return <></>;
    }
    if (isMessageDescriptor(description)) {
      return <FormattedMessage {...description} />;
    }
    return description;
  };

  return (
    <Stack alignInline={'center'} space={'space.600'}>
      <Stack alignInline={'center'} space={'space.100'}>
        <Image
          style={imageContainerStyling}
          src={ErrorImage}
          alt="error image"
        />
        <Heading size={'large'}>
          <FormattedMessage {...errorMessages.genericHeading} />
        </Heading>
        <Link href={RAISE_SUPPORT_TICKET_LINK}>
          <FormattedMessage {...errorMessages.genericFooter} />
        </Link>
      </Stack>
      <Stack space={'space.150'}>
        {clientName && (
          <Text weight="bold">
            <FormattedMessage
              {...errorMessages.genericErrorInformation}
              values={{ client: clientName.toUpperCase() }}
            />
          </Text>
        )}
        <Text>{descriptionElement()}</Text>
      </Stack>
    </Stack>
  );
};

function isMessageDescriptor(val: any): val is MessageDescriptor {
  return val && typeof val === 'object' && val.id && val.defaultMessage;
}

export default GenericErrorPage;
